import type {ContainerQueryObject} from '@Hooks/useContainerQuery';
import type {
  CreatePosterFromEmailMarketingCampaignAjaxParams,
  EmailCampaign,
  EmailCampaignId,
  EmailCampaignsLazyData,
  EmailCampaignSortingFilter,
  EmailCampaignStatusFilter,
  EmailMarketingCampaignsReduxState,
  EmailPreviewPollFinishResponse,
  UserImageHashedFileName,
  UserMediaHashedFileNameOrEmailCampaignId,
} from '@Components/email-marketing-campaigns/email-marketing-campaigns.types';
import {EmailCampaignSorting, EmailCampaignState, EmailCampaignStatus, EmailMarketingCampaignPageType} from '@Components/email-marketing-campaigns/email-marketing-campaigns.types';
import {getDateFromUnixTimestamp, getUnixTimestamp, isFutureDate, isInThirtyMinutesOrLessFromNow} from '@Utils/date.util';
import {noop} from '@Utils/general.util';
import {LoadingStates} from '@Utils/loading.util';
import {sendScheduledCampaignNow, unscheduleEmailCampaign} from '@Components/email-marketing-campaigns/email-marketing-campaigns-thunk';
import {executeThunk} from '@Utils/thunk.util';
import {onAjaxError} from '@Utils/ajax.util';
import {getUserId, isUserLoggedIn, isUserPremium} from '@Libraries/user.library';
import type {PosterHashedId} from '@Components/social-media/publish-params.types';
import type {UserVideoVOResponse} from '@Libraries/user-video-library';
import {runUserVideoUploadJob} from '@Libraries/user-media-library';
import type {PollKickoffAck} from '@Libraries/pollable-job.library.types';
import {openEmailCampaignDialogModal} from '@Modals/email-campaign-dialog-modal';
import type {CoreJsModalButtons} from '@/types/core-js-modal-buttons';
import {GA4EventName, GA4EventParamName} from '@Libraries/ga-events';
import type {MailingListVO} from '@Components/email-marketing-campaign-audience/email-marketing-campaign-audience.types';
import type {AudienceMailingList} from '@Components/mailing-list-wizard/mailing-list-wizard.types';
import type {Component, Editor} from 'grapesjs';
import {EmailCampaignAiRewriteSource, EmailMakerAiRewriteMode} from '@Libraries/ai-email-campaign-content-library';
import type {ImageComponentType} from '@Libraries/email-campaign-ai-image-regeneration-library';

const MOBILE_VERSION_BREAKPOINT = 768;

export enum EmailCampaignEvent {
  DELETED = 'email-campaigns::email-campaign-deleted',
  DUPLICATED = 'email-campaigns::email-campaign-duplicated',
}

export interface EmailCampaignCustomEventParams {
  id: EmailCampaignId;
  campaign?: EmailCampaign;
}

export type EmailCampaignCustomEvent = CustomEvent<EmailCampaignCustomEventParams>;

export interface GetEmailTemplateHTMLResponse {
  html: string;
  fileName: string;
}

export interface EmailMarketingCampaignInsights {
  idEmailMarketingCampaign: string;
  numRecipients: number;
  numDelivered: number;
  numOpens: number;
  numClicks: number;
  numBounces: number;
  numUnsubscribes: number;
  numComplaints: number;
}

export interface UpdateEmailCampaignContentAjaxParams {
  mailingListIds: number[];
  idEmailMarketingCampaign: EmailCampaignId;
  subject: string;
  preheader: string;
  name: string;
  idEmailTemplate: string | undefined;
  textContent: string | null;
  linkOnDesign: string | null;
}

export interface TextComponentData {
  ccid: string;
  content: string;
}

export interface ImageComponentData {
  ccid: string;
  type: ImageComponentType;
  url: string | undefined;
  height: number | undefined;
  width: number | undefined;
}

export const EMAIL_NON_PREVIEW_SKELETON_HEIGHT = 16;
export const EMAIL_STATUS_PILL_SKELETON_WIDTH = 62;
export const EMAIL_STATUS_PILL_SKELETON_HEIGHT = 26;

export const EMAIL_REPUTATION_PILL_SKELETON_HEIGHT = 24;
export const EMAIL_REPUTATION_PILL_SKELETON_WIDTH = 24;

export const EMAIL_CAMPAIGNS_LAZY_BATCH_SIZE = 30;

const EMAIL_EXPORT_GA_LABEL_MY_STUFF = 'MyStuff';
const SEND_CAMPAIGN_NOW_MODAL_ID = 'send-campaign-now';
const UNSCHEDULE_CAMPAIGN_MODAL_ID = 'cancel-scheduled-campaign';
// the minimum number of minutes a campaign needs to be scheduled from now to be considered 'scheduled'
const MIN_SCHEDULE_MINUTES_FROM_NOW = 15;

export const myStuffContainerQuery: ContainerQueryObject = {
  xxsmall: {
    maxWidth: 400,
  },

  xsmall: {
    maxWidth: 510,
  },

  small: {
    minWidth: 511,
    maxWidth: 750,
  },
  medium: {
    minWidth: 751,
    maxWidth: 1024,
  },
  large: {
    minWidth: 1024,
  },
};

export const isEmailCampaignOwnedByCurrentUser = (campaign: EmailCampaign): boolean => {
  return campaign.userId === getUserId();
};
export const isEmailCampaignSharedByTeamMember = (campaign: EmailCampaign): boolean => {
  return campaign.isSharedInTeam && campaign.userId !== getUserId();
};

// Declared as a hoisted function (not a const arrow): email-marketing-campaigns-slice imports this and
// calls it while building its initialState, and slice ← thunk ← this library form an import cycle. A const
// arrow would still be in the TDZ when the slice evaluates first, throwing "not a function"; a hoisted
// function is initialised at module instantiation, so it survives the cycle. Same for the two defaults below.
export function getCampaignsInitialLazyData(): EmailCampaignsLazyData {
  return {
    ids: [],
    loadingState: LoadingStates.NOT_LOADED,
    loadMore: true,
  };
}

export const getDummyEmailCampaign = (): EmailCampaign => {
  return {
    id: '',
    state: EmailCampaignState.DRAFT,
    subject: '',
    isScheduled: false,
    isPaused: false,
    isVideo: false,
    isDeleted: false,
    isEditable: false,
    imagePreviewURL: '',
    name: '',
    publishOn: 0,
    mailingListNames: [],
    lastModified: 0,
    mailingLists: [],
    preheader: '',
    isSharedInTeam: false,
    userId: -1,
  };
};

export const getDummyMailingList = (): AudienceMailingList => {
  return {
    id: -1,
    name: '',
    emailIds: [],
    totalEmailsCount: 0,
    filters: {},
    searchTerm: '',
    isSpam: false,
    idOwner: -1,
    lastViewed: 0,
    isSharedInTeam: false,
    isSmart: false,
  };
};

export const createPosterFromEmailCampaignId = async (idEmailMarketingCampaign: string, onStart: VoidFunction = noop, onFinish: VoidFunction = noop): Promise<string> => {
  onStart();
  const isVideo = await isEmailMarketingCampaignVideo(idEmailMarketingCampaign);
  let hashedFileName: UserImageHashedFileName;

  if (isVideo) {
    hashedFileName = (await publishVideoEmailCampaignToSocialMedia(idEmailMarketingCampaign)).filename;
  } else {
    hashedFileName = await publishImageEmailCampaignToSocialMedia(idEmailMarketingCampaign);
  }

  onFinish();

  return createPosterFromEmailMarketingCampaign({
    hashedFileName,
    idEmailMarketingCampaign,
  });
};

export const publishEmailCampaignToSocialMediaWithPosterId = (posterId: PosterHashedId): void => {
  window.location.href = window.PMW.util.site_url(`socialmedia/post/design/${posterId}`);
};

export const publishEmailCampaignToSocialMedia = async (idEmailMarketingCampaign: string, onStart: VoidFunction = noop, onFinish: VoidFunction = noop): Promise<void> => {
  onStart();
  const posterHashedId = await createPosterFromEmailCampaignId(idEmailMarketingCampaign);
  onFinish();
  publishEmailCampaignToSocialMediaWithPosterId(posterHashedId);
};

const publishImageEmailCampaignToSocialMedia = async (idEmailMarketingCampaign: string): Promise<string> => {
  return (await window.PMW.pollingAjaxCallAsync(
    uploadEmailMarketingCampaignAsUserImage.bind(null, idEmailMarketingCampaign),
    pollHasUserImageUploaded
  )) as UserMediaHashedFileNameOrEmailCampaignId;
};
interface EmailCampaignVideoAlreadyRenderedResponse {
  hashedPosterId: string;
}

type EmailCampaignVideoUploadKickoffResponse = PollKickoffAck | EmailCampaignVideoAlreadyRenderedResponse;

/**
 * emailCampaignHTMLUploadV2 short-circuits with a bare `{hashedPosterId}` (no `pollingKey`) when
 * the campaign's HTML has already been rendered to a poster by a prior publish; only the ordinary
 * kickoff ACK carries a `pollingKey` for {@link runUserVideoUploadJob}'s pollableJob to poll
 * against. The legacy equivalent of this short-circuit polled `postervideo/getUserVideoStatus`
 * with the poster's hashed id as if it were a user-video hashed filename, which can never match a
 * video record: it silently hung for the full 30-minute client timeout and then threw on
 * `undefined.filename`. Detecting the shape here and returning immediately avoids reproducing that
 * hang; only `filename` is modeled below since `createPosterFromEmailCampaignId` never reads
 * anything else off the resolved value, and `createPosterFromEmailMarketingCampaignMediaItem`
 * re-checks `getHashedPosterId()` itself and ignores the `hashedFilename` it's sent once a poster
 * already exists. The guard tests positively for `hashedPosterId` rather than the absence of
 * `pollingKey`, so an unexpected pollingKey-less success shape fails loudly instead of silently
 * resolving to `{filename: undefined}`.
 */
const isEmailCampaignAlreadyRenderedResponse = (response: EmailCampaignVideoUploadKickoffResponse): response is EmailCampaignVideoAlreadyRenderedResponse => {
  return 'hashedPosterId' in response;
};

const publishVideoEmailCampaignToSocialMedia = async (idEmailMarketingCampaign: string): Promise<Pick<UserVideoVOResponse, 'filename'>> => {
  const response = await uploadEmailMarketingCampaignAsUserVideo(idEmailMarketingCampaign);
  if (isEmailCampaignAlreadyRenderedResponse(response)) {
    return {filename: response.hashedPosterId};
  }
  return runUserVideoUploadJob(() => Promise.resolve(response));
};

export const isDeletedCampaignsViewType = (campaignViewType: EmailMarketingCampaignPageType): boolean => {
  return campaignViewType === EmailMarketingCampaignPageType.DELETED;
};

export const isDefaultCampaignsViewType = (campaignViewType: EmailMarketingCampaignPageType): boolean => {
  return campaignViewType === EmailMarketingCampaignPageType.OWN;
};

export const isMyEmailsCampaignsGridViewType = (campaignViewType: EmailMarketingCampaignPageType): boolean => {
  return isDefaultCampaignsViewType(campaignViewType) || isSharedCampaignsViewType(campaignViewType);
};

export const isSharedCampaignsViewType = (campaignViewType: EmailMarketingCampaignPageType): boolean => {
  return campaignViewType === EmailMarketingCampaignPageType.SHARED_WITH_ME;
};

export const getCampaignPublishWizardUri = (campaign: EmailCampaign): string => {
  return `emailmarketing/publish/campaign/${campaign.id}`;
};

export const getScheduledCampaignPublishWizardUri = (campaign: EmailCampaign): string => {
  return `emailmarketing/publish/campaign/${campaign.id}/scheduled`;
};

export const getRedirectToEmailGalleryURL = (): string => {
  return window.PMW.util.site_url('email/types');
};

export const openCampaignEditingPage = (campaign: EmailCampaign): void => {
  if (campaign.emailTemplateId) {
    window.location.href = window.PMW.util.site_url(`emailmaker/load/${campaign.emailTemplateId}`);
    return;
  }

  if (campaign.posterId) {
    openCampaignInWizard(getCampaignPublishWizardUri(campaign));
  }
};

export const openCampaignInWizard = (redirectURL: string): void => {
  if (isUserPremium()) {
    window.location.href = window.PMW.util.site_url(redirectURL);
    return;
  }

  openUpsellingDialogForEmailCampaigns();
};

export const openUpsellingDialogForEmailCampaigns = (): void => {
  void window.PMW.showPremiumOnlyFeatureDialog(window.PMW.PREMIUM_ONLY_FEATURE_NAME_PUBLISH_EMAIL_CAMPAIGN);
};

export const onCreateNewCampaign = (): void => {
  window.location.href = getRedirectToEmailGalleryURL();
};

export const isDraftCampaign = (campaign: EmailCampaign): boolean => {
  return campaign.state === EmailCampaignState.DRAFT;
};

export const isCampaignDeleted = (campaign: EmailCampaign): boolean => {
  return campaign.isDeleted;
};

export const isCampaignPaused = (campaign: EmailCampaign): boolean => {
  return campaign.isPaused;
};

export const isCampaignSent = (campaign: EmailCampaign): boolean => {
  return campaign.state === EmailCampaignState.SENT || campaign.state === EmailCampaignState.FAKE_SENT;
};

export const isCampaignCurrentlyScheduled = (campaign: EmailCampaign): boolean => {
  return !isCampaignSent(campaign) && campaign.isScheduled && !isCampaignPublishTimePastOrApproaching(campaign);
};

export const isCampaignReadyToSend = (campaign: EmailCampaign): boolean => {
  return campaign.state === EmailCampaignState.READY;
};

export const isCampaignSending = (campaign: EmailCampaign): boolean => {
  return (
    campaign.state === EmailCampaignState.SENDING ||
    campaign.state === EmailCampaignState.BATCH_READY ||
    campaign.state === EmailCampaignState.FAKE_SENDING ||
    campaign.state === EmailCampaignState.WAITING_FOR_RETRY
  );
};

export const isCampaignPreparingToSend = (campaign: EmailCampaign): boolean => {
  return campaign.state === EmailCampaignState.PREPARING_GRAPHIC || campaign.state === EmailCampaignState.VALIDATING_MAILING_LIST;
};

export const isCampaignValidating = (campaign: EmailCampaign): boolean => {
  return campaign.state === EmailCampaignState.UNDER_REVIEW || campaign.state === EmailCampaignState.UNDER_SUSPICION;
};

export const isCampaignPreparingNextBatch = (campaign: EmailCampaign): boolean => {
  return campaign.state === EmailCampaignState.BATCH_PAUSED;
};

export const isCampaignSuspended = (campaign: EmailCampaign): boolean => {
  return campaign.state === EmailCampaignState.SUSPENDED || campaign.state === EmailCampaignState.ADMIN_ARCHIVED || campaign.state === EmailCampaignState.SPAM_CONTENT;
};

export const hasCampaignGenerationFailed = (campaign: EmailCampaign): boolean => {
  return campaign.state === EmailCampaignState.GENERATION_FAILED;
};

export const isCampaignPausable = (campaign: EmailCampaign): boolean => {
  return !campaign.isPaused && (isCampaignPartOfSendingLifeCycle(campaign) || hasCampaignGenerationFailed(campaign));
};

export const isCampaignPartOfSendingLifeCycle = (campaign: EmailCampaign): boolean => {
  return isCampaignSending(campaign) || isCampaignPreparingToSend(campaign) || isCampaignPreparingNextBatch(campaign);
};
export const isCampaignInSendingOrValidationLifecycle = (campaign: EmailCampaign): boolean => {
  return isCampaignPartOfSendingLifeCycle(campaign) || isCampaignReadyToSend(campaign) || isCampaignValidating(campaign);
};
export const hasCampaignPublishOnPassed = (publishOnTimestamp: number): boolean => {
  return !isFutureDate(getDateFromUnixTimestamp(publishOnTimestamp));
};

export const isCampaignPublishTimePastOrApproaching = (campaign: EmailCampaign): boolean => {
  const publishDate = getDateFromUnixTimestamp(campaign.publishOn);
  return hasCampaignPublishOnPassed(campaign.publishOn) || isInThirtyMinutesOrLessFromNow(publishDate);
};

export function getDefaultEmailStatusFilters(): EmailCampaignStatusFilter {
  return {
    [EmailCampaignStatus.DRAFT]: true,
    [EmailCampaignStatus.SCHEDULED]: true,
    [EmailCampaignStatus.SENDING]: true,
    [EmailCampaignStatus.PUBLISHED]: true,
    [EmailCampaignStatus.ERROR]: true,
  };
}

export function getDefaultEmailSortingFilter(): EmailCampaignSortingFilter {
  return EmailCampaignSorting.PUBLISHING_DATE;
}
export const startSendCampaignNowFlow = (campaignId: string): void => {
  const modalButtons: CoreJsModalButtons = {};

  modalButtons[window.i18next.t('pmwjs_btn_cancel')] = {
    ch: (): void => {
      window.PMW.closeModalDialog(SEND_CAMPAIGN_NOW_MODAL_ID);
    },
    classes: ' secondary back-cta',
  };

  modalButtons[window.i18next.t('pmwjs_send_right_now')] = {
    ch() {
      window.PMW.setLoading(window.i18next.t('pmwjs_loading'));
      void executeThunk(
        () => {
          return window.PMW.redux.store.dispatch(sendScheduledCampaignNow(campaignId));
        },
        window.PMW.setLoading.bind(null, null)
      );

      window.PMW.closeModalDialog(SEND_CAMPAIGN_NOW_MODAL_ID);
    },
    classes: '-full-width send-campaign-cta spacing-m-r-6',
  };

  const dialogContent = window.PMW.openModalDialog(SEND_CAMPAIGN_NOW_MODAL_ID, window.i18next.t('pmwjs_send_right_now'), modalButtons, true, '-modal-550', false).find('.content');
  window.PMW.load(dialogContent, window.PMW.util.site_url('emailmarketing/sendEmailCampaignNowModal'), {});
};

export const startUnScheduleCampaignNowFlow = (campaignId: string): void => {
  const modalButtons: CoreJsModalButtons = {};

  modalButtons[window.i18next.t('pmwjs_go_back')] = {
    ch: (): void => {
      window.PMW.closeModalDialog(UNSCHEDULE_CAMPAIGN_MODAL_ID);
    },
    classes: ' secondary back-cta spacing-m-b-2',
  };

  modalButtons[window.i18next.t('pmwjs_yes_cancel')] = {
    ch: (): void => {
      window.PMW.setLoading(window.i18next.t('pmwjs_loading'));
      void executeThunk(
        () => {
          return window.PMW.redux.store.dispatch(unscheduleEmailCampaign(campaignId));
        },
        window.PMW.setLoading.bind(null, null)
      );

      window.PMW.closeModalDialog(UNSCHEDULE_CAMPAIGN_MODAL_ID);
    },
    classes: '-full-width cancel-campaign',
  };

  const dialogContent = window.PMW.openModalDialog(
    UNSCHEDULE_CAMPAIGN_MODAL_ID,
    window.i18next.t('pmwjs_cancelled_scheduled_campaign'),
    modalButtons,
    true,
    '-modal-550',
    false
  ).find('.content');
  window.PMW.load(dialogContent, window.PMW.util.site_url('emailmarketing/cancelScheduledEmailCampaignNowModal'), {});
};

export const areEmailCampaignFiltersInDefaultState = (statusFilter: EmailCampaignStatusFilter, sortingFilter: EmailCampaignSortingFilter): boolean => {
  return sortingFilter === EmailCampaignSorting.PUBLISHING_DATE && areAllStatusesSelectedInFilter(statusFilter);
};

export const areAllStatusesSelectedInFilter = (statusFilter: EmailCampaignStatusFilter): boolean => {
  return (
    Object.values(statusFilter).find((val) => {
      return !val;
    }) === undefined
  );
};

export const getSelectedStatusesInFilter = (statusFilter: EmailCampaignStatusFilter): EmailCampaignStatus[] => {
  return (Object.keys(statusFilter) as EmailCampaignStatus[]).filter((filterKey) => {
    return statusFilter[filterKey];
  });
};
export const getAppropriateEmailCampaignsLazyData = (
  state: EmailMarketingCampaignsReduxState,
  statusFilter: EmailCampaignStatusFilter,
  sortingFilter: EmailCampaignSortingFilter
): EmailCampaignsLazyData => {
  if (!isMyEmailsCampaignsGridViewType(state.viewType) || areEmailCampaignFiltersInDefaultState(statusFilter, sortingFilter)) {
    return state.defaultCampaignsLazyData;
  }

  return state.filteredCampaignsLazyData;
};

const getReadablePublishingDate = (campaign: EmailCampaign): string => {
  const publishOnTimestamp = getDateFromUnixTimestamp(campaign.publishOn);

  return publishOnTimestamp.toLocaleString('en-US', {
    month: 'short',
    day: '2-digit',
    year: 'numeric',
  });
};

const getReadablePublishingTime = (campaign: EmailCampaign): string => {
  const publishOnTimestamp = getDateFromUnixTimestamp(campaign.publishOn);
  return publishOnTimestamp.toLocaleString('en-US', {
    hour: '2-digit',
    minute: '2-digit',
    hour12: true,
  });
};

export const getCampaignPublishStatusDate = (campaign: EmailCampaign): string => {
  return isDraftCampaign(campaign) ? window.i18next.t('pmwjs_none_draft') : getReadablePublishingDate(campaign);
};

export const getCampaignPublishStatusTime = (campaign: EmailCampaign): string => {
  return isDraftCampaign(campaign) ? window.i18next.t('pmwjs_none_draft') : getReadablePublishingTime(campaign);
};

export const copyEmailCampaignHTML = (emailTemplateId?: string): void => {
  if (!emailTemplateId) {
    return;
  }

  window.PMW.setLoading(window.i18next.t('pmwjs_copying'));
  loadExportEmailDependencies(async () => {
    try {
      const response = await getEmailTemplateHTML(emailTemplateId);
      doCopyEmailTemplateHTML(response);
    } catch (err) {
      onAjaxError(err);
    } finally {
      window.PMW.setLoading(null);
    }
  });
};

export const downloadEmailCampaignHTML = (emailTemplateId?: string): void => {
  if (!emailTemplateId) {
    return;
  }

  window.PMW.setLoading(window.i18next.t('pmwjs_downloading'));
  loadExportEmailDependencies(async () => {
    try {
      const response = await getEmailTemplateHTML(emailTemplateId);
      doDownloadEmailTemplateHTML(response);
    } catch (err) {
      onAjaxError(err);
    } finally {
      window.PMW.setLoading(null);
    }
  });
};

export const getMinDateForChangingScheduleTime = (): Date => {
  const minDate = new Date();
  minDate.setMinutes(minDate.getMinutes() + MIN_SCHEDULE_MINUTES_FROM_NOW);
  return minDate;
};

export const getCampaignNameText = (campaign: EmailCampaign): string => {
  return campaign.name && campaign.name !== '' ? campaign.name : window.i18next.t('pmwjs_no_title');
};

export const openCampaignRescheduledSuccessfulyModal = (newDate: Date): void => {
  const dialogContent = window.PMW.openModalDialog('reschedule-campaign', window.i18next.t('pmwjs_rescheduled_success'), {}, true, '-modal-550', false).find('.content');
  window.PMW.load(dialogContent, window.PMW.util.site_url('emailmarketing/rescheduleEmailCampaignModal'), {
    timestamp: getUnixTimestamp(newDate),
  });
};

export const pollForEmailTemplatePreview = (emailTemplateId: string, onSuccess: (response?: string) => void = noop, onError: VoidFunction = noop): void => {
  window.PMW.pollingAjaxCallAsync(
    window.PMW.readLocal.bind(null, 'emailmarketing/isEmailTemplatePreviewGenerated', {
      emailTemplateId,
    }),
    emailTemplatePreviewPollingFunction.bind(null, emailTemplateId),
    20000
  ).then(onSuccess, onError);
};

const emailTemplatePreviewPollingFunction = async (emailTemplateId: string, data: string | null): Promise<EmailPreviewPollFinishResponse> => {
  if (data) {
    return {
      isAvailable: true,
      data,
    };
  }

  return window.PMW.readLocal('emailmarketing/checkEmailTemplatePreviewGenerationStatus', {
    emailTemplateId,
  }) as Promise<EmailPreviewPollFinishResponse>;
};

const getEmailTemplateHTML = async (emailTemplateId: string): Promise<GetEmailTemplateHTMLResponse> => {
  return (await window.PMW.readLocal('emailmarketing/getEmailTemplateHTML', {
    idEmailTemplate: emailTemplateId,
  })) as Promise<GetEmailTemplateHTMLResponse>;
};

const doCopyEmailTemplateHTML = (response: GetEmailTemplateHTMLResponse): void => {
  void window.PMW.copyEmailHTML(response.html, noop, EMAIL_EXPORT_GA_LABEL_MY_STUFF, showCopiedToast);
};

const doDownloadEmailTemplateHTML = (response: GetEmailTemplateHTMLResponse): void => {
  window.PMW.downloadEmailHTML(response.html, response.fileName, EMAIL_EXPORT_GA_LABEL_MY_STUFF);
};

const showCopiedToast = (): void => {
  window.PMW.setLoading(window.i18next.t('pmwjs_copied'), true);
  setTimeout(window.PMW.setLoading.bind(null, null), 3000);
};

const loadExportEmailDependencies = (onLoad: VoidFunction): void => {
  window.PMW.util.require(typeof window.PMW.copyEmailHTML === 'undefined', 'exportEmailTemplate', false, () => {
    onLoad();
  });
};

const pollHasUserImageUploaded = (hashedFileName: UserMediaHashedFileNameOrEmailCampaignId): Promise<UserMediaHashedFileNameOrEmailCampaignId> => {
  return window.PMW.writeLocal('posterimage/checkHasUserImageUploaded', {
    hashedFileName,
  }) as Promise<UserMediaHashedFileNameOrEmailCampaignId>;
};

const uploadEmailMarketingCampaignAsUserImage = (campaignId: EmailCampaignId): Promise<UserMediaHashedFileNameOrEmailCampaignId> => {
  return window.PMW.writeLocal('posterimage/emailCampaignHTMLUpload', {
    idEmailMarketingCampaign: campaignId,
  }) as Promise<UserMediaHashedFileNameOrEmailCampaignId>;
};

const uploadEmailMarketingCampaignAsUserVideo = (campaignId: EmailCampaignId): Promise<EmailCampaignVideoUploadKickoffResponse> => {
  return window.PMW.writeLocal('postervideo/emailCampaignHTMLUploadV2', {
    idEmailMarketingCampaign: campaignId,
  }) as Promise<EmailCampaignVideoUploadKickoffResponse>;
};

const isEmailMarketingCampaignVideo = (campaignId: EmailCampaignId): Promise<boolean> => {
  return window.PMW.writeLocal('emailmarketing/isEmailMarketingCampaignVideo', {
    idEmailMarketingCampaign: campaignId,
  }) as Promise<boolean>;
};

const createPosterFromEmailMarketingCampaign = (opts: CreatePosterFromEmailMarketingCampaignAjaxParams): Promise<PosterHashedId> => {
  return window.PMW.writeLocal('emailmaker/createPosterFromEmailMarketingCampaignMediaItem', {
    idEmailMarketingCampaign: opts.idEmailMarketingCampaign,
    hashedFilename: opts.hashedFileName,
  }) as Promise<PosterHashedId>;
};

export const getEmailMarketingCampaignInsightsByCampaignId = async (campaignId: string): Promise<EmailMarketingCampaignInsights> => {
  return (await window.PMW.readLocal('emailmarketing/getEmailMarketingCampaignInsightsByCampaignId', {
    campaignId,
  })) as Promise<EmailMarketingCampaignInsights>;
};

export const getEmailMarketingCampaignById = async (campaignId: string): Promise<EmailCampaign | undefined> => {
  return (await window.PMW.readLocal('emailmarketing/getEmailMarketingCampaign', {
    campaignId,
  })) as EmailCampaign | undefined;
};

export const shareEmailMarketingCampaignInTeam = async (isSharedInTeam: boolean, idEmailMarketingCampaign: string): Promise<void> => {
  if (isUserLoggedIn()) {
    await window.PMW.writeLocal('emailmarketing/updateEmailMarketingCampaignSharingStatus', {
      shareInTeam: isSharedInTeam,
      idEmailMarketingCampaign: idEmailMarketingCampaign,
    });
  }
};

export const isMailingListSharedByTeamMember = (mailingList: AudienceMailingList): boolean => {
  return mailingList.isSharedInTeam && mailingList.idOwner !== getUserId();
};

export const isMailingListShareableInTeam = (mailingList: AudienceMailingList): boolean => {
  if (mailingList.isSmart) {
    return false;
  }

  if (mailingList.isSharedInTeam && getUserId() !== mailingList.idOwner) {
    return false;
  }

  return true;
};

export const shareMailingListInTeam = async (isSharedInTeam: boolean, idMailingList: number): Promise<void> => {
  if (isUserLoggedIn()) {
    await window.PMW.writeLocal('emailmarketing/updateMailingListSharingStatus', {
      shareInTeam: isSharedInTeam,
      idMailingList,
    });
  }
};

export const openEmailMarketingCampaignDialog = async (campaignId: string): Promise<void> => {
  window.PMW.setLoading(window.i18next.t('pmwjs_loading'));
  const data = (await window.PMW.readLocal('emailmarketing/getEmailMarketingCampaign', {
    campaignId,
  })) as EmailCampaign | undefined;
  window.PMW.setLoading(null);

  if (!data) {
    return;
  }

  openEmailCampaignDialogModal({campaign: data});
};

export const trackGA4NumberOfSectionsInEmailCampaign = (campaignId: string): void => {
  void window.PMW.readLocal('emailmaker/getEmailCampaignSectionCount', {idEmailCampaign: campaignId}).then((response: {sectionsCount: number}) => {
    window.PMW.gtm.trackGA4CustomEvent(GA4EventName.EMAILMAKER_SECTIONS_PUBLISHED, {[GA4EventParamName.VALUE]: response.sectionsCount});
  });
};

export const getMailingListIdsFromCampaign = (campaign: EmailCampaign): number[] => {
  return campaign.mailingLists.map((mailingList) => {
    return mailingList.id;
  });
};

export const getAjaxParamsForUpdatingEmailCampaignContent = (campaign: EmailCampaign, textContent = null, linkOnDesign = null): UpdateEmailCampaignContentAjaxParams => {
  return {
    mailingListIds: getMailingListIdsFromCampaign(campaign),
    idEmailMarketingCampaign: campaign.id,
    subject: campaign.subject,
    preheader: campaign.preheader,
    name: campaign.name,
    idEmailTemplate: campaign.emailTemplateId,
    textContent,
    linkOnDesign,
  };
};

export const getEmailSendsRequiredForSendingCampaign = (campaign: EmailCampaign): number => {
  return campaign.mailingLists.reduce((totalEmails: number, mailingList: MailingListVO): number => {
    return totalEmails + mailingList.validEmailsCount;
  }, 0);
};

export const canUserSendOutEmailCampaign = (numEmailSendsAvailable: number, campaign: EmailCampaign): boolean => {
  return numEmailSendsAvailable >= getEmailSendsRequiredForSendingCampaign(campaign);
};

export const isUserCampaignCreator = (userId: number, campaign: EmailCampaign): boolean => {
  return campaign.userId === userId;
};

export const isMobileWidth = (): boolean => {
  return window.innerWidth <= MOBILE_VERSION_BREAKPOINT;
};

export const convertComponentIfNeeded = (
  editor: Editor,
  component: Component,
  expectedOriginalComponentType: string,
  newComponentBlockId: string,
  selectNewComponent = true,
  removeOldComponent = true
): Component => {
  if (!component.is(expectedOriginalComponentType)) {
    return component;
  }

  const parent = component.parent() ?? (editor.runCommand(window.PMW.emails.commands.GET_MAIN_WRAPPER) as Component);
  const index = component.index();
  const newComponentDefinition = editor.BlockManager.get(newComponentBlockId).getContent();

  if (!newComponentDefinition) {
    return component;
  }

  const newComponent = parent.append(newComponentDefinition, {at: index})[0];
  newComponent.setStyle(component.getStyle());

  editor.runCommand(window.PMW.emails.commands.SET_IMAGE_LOADING, {
    component,
    loading: false,
  });

  if (removeOldComponent) editor.runCommand(window.PMW.emails.commands.REMOVE_COMPONENT, {component});
  if (selectNewComponent) editor.select(newComponent);

  return newComponent;
};
function getTextComponentsToRewrite(editor: Editor, rewriteMode: EmailMakerAiRewriteMode): Component[] | undefined {
  let selectedComponent: Component | undefined;

  switch (rewriteMode) {
    case EmailMakerAiRewriteMode.REWRITE_ALL_TEXT_COMPONENTS:
      return editor.getWrapper()?.find(`[data-pmw-type="${window.PMW.emails.components.text.PMW_TEXT}"]`);
    case EmailMakerAiRewriteMode.REWRITE_SELECTED_TEXT_COMPONENT:
      selectedComponent = editor.getSelected();

      if (!selectedComponent) {
        return undefined;
      }

      if (selectedComponent.attributes.type === window.PMW.emails.components.text.PMW_BUTTON) {
        selectedComponent = selectedComponent.find(`[data-pmw-type="${window.PMW.emails.components.text.PMW_TEXT}"]`)[0];
      }

      return selectedComponent ? [selectedComponent] : undefined;
    default:
      return undefined;
  }
}

export function getEmailTextWithCcid(editor: Editor, rewriteSource: EmailCampaignAiRewriteSource = EmailCampaignAiRewriteSource.EMAIL_MAKER_AI_REDESIGN): TextComponentData[] {
  let rewriteMode: EmailMakerAiRewriteMode;

  switch (rewriteSource) {
    case EmailCampaignAiRewriteSource.EMAIL_MAKER_AI_REWRITE:
    case EmailCampaignAiRewriteSource.EMAIL_MAKER_AI_REWRITE_DROPDOWN:
      rewriteMode = EmailMakerAiRewriteMode.REWRITE_SELECTED_TEXT_COMPONENT;
      break;
    case EmailCampaignAiRewriteSource.EMAIL_MAKER_AI_REDESIGN:
    default:
      rewriteMode = EmailMakerAiRewriteMode.REWRITE_ALL_TEXT_COMPONENTS;
      break;
  }

  const componentsData: TextComponentData[] = [];
  const textComponents = getTextComponentsToRewrite(editor, rewriteMode);

  if (textComponents) {
    textComponents.forEach((component) => {
      const componentId = component.ccid;
      const componentContent = component.getEl()?.innerHTML;
      componentsData.push({ccid: componentId, content: componentContent ?? ''});
    });
  }

  return componentsData;
}
